/* Client-side mirror of firestore.rules' rolling-window rate-limit counters
 * (`improvementLimits/{uid}`, `messageLimits/{uid}`, `joinRequestLimits/{uid}`).
 *
 * The rules are the real enforcement — each rate-limited create must be committed in the SAME
 * write as an update to the caller's counter doc, and the counter's own rules only accept either a
 * fresh window (`windowStartAt == request.time`, `count == 1`) or exactly +1 inside the open
 * window, capped at the collection's max. This module only decides which of those two shapes a
 * well-behaved client should write, so it can fail fast with a friendly message instead of a bare
 * permission-denied. Pure and dependency-free so it can be unit tested under plain Node. */

export const RATE_WINDOW_MS = 60_000

export interface LimiterState {
  /** Epoch millis of the window start, as read back from the counter doc's `windowStartAt`. */
  windowStartMs: number
  count: number
}

export type LimiterDecision =
  | { kind: 'new-window' }
  | { kind: 'increment'; count: number }
  | { kind: 'blocked'; retryAfterMs: number }

/** `force` lets a caller retry with the opposite branch when its clock disagrees with the
 *  server's about whether the window has elapsed (the rules use server time, not the client's). */
export function decideLimiter(
  prev: LimiterState | null,
  nowMs: number,
  max: number,
  force?: 'new-window' | 'increment',
): LimiterDecision {
  if (force === 'new-window') return { kind: 'new-window' }
  if (!prev) return { kind: 'new-window' }
  const elapsed = nowMs - prev.windowStartMs
  if (force !== 'increment' && elapsed >= RATE_WINDOW_MS) return { kind: 'new-window' }
  if (prev.count >= max) {
    return { kind: 'blocked', retryAfterMs: Math.max(0, RATE_WINDOW_MS - elapsed) }
  }
  return { kind: 'increment', count: prev.count + 1 }
}

export function retryAfterSeconds(retryAfterMs: number): number {
  return Math.max(1, Math.ceil(retryAfterMs / 1000))
}
